// UTC offsets and local wall-clock conversion from the compiled tz tables. No Date, no Intl.
import { civilFromDays, daysFromCivil, daysInMonth, MS_PER_DAY, SECONDS_PER_DAY, weekdayOfDays } from './calendar.js';
import type { RulePart, ZoneTable } from './tzdata/types.js';
import { TZ_EXPLICIT_UNTIL_SEC, ZONE_IDS, ZONE_TABLES, type ZoneId } from './tzdata/zones.generated.js';
import type { InstantInput } from './types.js';

/** An IANA zone id that has a compiled table (see scripts/gen-tz.ts for the list). */
export type IanaZone = ZoneId;

export function isSupportedZone(zone: string): zone is IanaZone {
  return (ZONE_IDS as readonly string[]).includes(zone);
}

/** Normalizes an instant to integer epoch milliseconds (a safe-integer number; floors fractional input). */
export function toEpochMs(at: InstantInput): number {
  return typeof at === 'bigint' ? Number(at) : Math.floor(at);
}

/** UTC seconds at which a rule part fires in `year`, given the offset in effect just before it. */
function ruleTransitionSec(year: number, part: RulePart, offsetBefore: number): number {
  const [month, week, weekday, wallSeconds] = part;
  const first = daysFromCivil(year, month, 1);
  let day = 1 + ((weekday - weekdayOfDays(first) + 7) % 7) + (week - 1) * 7;
  while (day > daysInMonth(year, month)) day -= 7; // week 5 means the last such weekday
  return (first + day - 1) * SECONDS_PER_DAY + wallSeconds - offsetBefore;
}

function offsetFromTable(table: ZoneTable, sec: number): number {
  const t = table.transitions;
  let lo = 0;
  let hi = t.length / 2 - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if ((t[mid * 2] ?? 0) <= sec) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found < 0 ? table.initialOffset : (t[found * 2 + 1] ?? table.initialOffset);
}

/** Offset in seconds east of UTC in effect at `sec` (UTC seconds since the epoch). */
export function offsetSecondsAt(zone: IanaZone, sec: number): number {
  const table = ZONE_TABLES[zone];
  const rule = table.rule;
  if (sec < TZ_EXPLICIT_UNTIL_SEC || rule === null) return offsetFromTable(table, sec);
  const year = civilFromDays(Math.floor((sec + rule.std) / SECONDS_PER_DAY)).year;
  const toDst = ruleTransitionSec(year, rule.toDst, rule.std);
  const toStd = ruleTransitionSec(year, rule.toStd, rule.dst);
  const inDst = toDst < toStd ? sec >= toDst && sec < toStd : sec >= toDst || sec < toStd;
  return inDst ? rule.dst : rule.std;
}

type LocalDateTimeParts = {
  /** Local calendar day number (days since 1970-01-01). */
  readonly days: number;
  /** Milliseconds since local midnight, 0 .. 86_399_999. */
  readonly msOfDay: number;
  readonly offsetSeconds: number;
};

/** The wall clock at an instant. */
export function toLocal(zone: IanaZone, epochMs: number): LocalDateTimeParts {
  const offsetSeconds = offsetSecondsAt(zone, Math.floor(epochMs / 1000));
  const localMs = epochMs + offsetSeconds * 1000;
  const days = Math.floor(localMs / MS_PER_DAY);
  return { days, msOfDay: localMs - days * MS_PER_DAY, offsetSeconds };
}

/**
 * The instant at which the wall clock in `zone` reads (days, secondsOfDay). DST policy (CD-B05):
 * - in a spring-forward gap the wall time never occurs, so it resolves to the first valid instant after the gap
 *   (the transition itself: a 02:30 start on a 02:00 -> 03:00 night begins at 03:00 daylight time);
 * - in a fall-back overlap the wall time occurs twice, so it resolves to the first (earlier, daylight) occurrence.
 * Assumes at most one offset change within a day of the requested wall time, which holds for every compiled zone.
 */
export function resolveLocal(zone: IanaZone, days: number, secondsOfDay: number): number {
  const localSec = days * SECONDS_PER_DAY + secondsOfDay;
  const before = offsetSecondsAt(zone, localSec - SECONDS_PER_DAY);
  const after = offsetSecondsAt(zone, localSec + SECONDS_PER_DAY);
  const candidates = [before, after]
    .map((o) => localSec - o)
    .filter((t) => offsetSecondsAt(zone, t) === localSec - t)
    .sort((a, b) => a - b);
  const first = candidates[0];
  if (first !== undefined) return first * 1000;
  // Gap: no instant reads this wall time. The transition lies in (localSec - after, localSec - before]; find it.
  let lo = Math.min(localSec - before, localSec - after);
  let hi = Math.max(localSec - before, localSec - after);
  const offsetLo = offsetSecondsAt(zone, lo);
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (offsetSecondsAt(zone, mid) === offsetLo) lo = mid;
    else hi = mid;
  }
  return hi * 1000;
}
